import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ReactApexChart from "react-apexcharts";
import { toast } from "react-toastify";
import styles from "./RevenueTracker.module.css";
import OverdueAmount from "./OverdueAmount";
import { fmt, fmtBillAmount, fmtBillDate } from "./format";
import { useSortableRows, SortableHeaderRow } from "./sortable";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Pretty labels for the buckets returned by the backend.
const INCOME_BUCKETS = [
  {
    key: "recurring_monthly",
    label: "Monthly recurring bills",
    hint: "Bill counted FULL in its month",
  },
  {
    key: "recurring_yearly_amortised",
    label: "Yearly recurring (÷12)",
    hint: "1/12 of each yearly bill, for 12 months from createdAt",
  },
  {
    key: "domain_amortised",
    label: "Easexpense Domains (÷12)",
    hint: "Domains billed yearly, same 1/12 split",
  },
  {
    key: "data_migration_one_shot",
    label: "Data migration (one-shot)",
    hint: "Counted FULL in createdAt month · pure income, no vendor cost",
  },
];

const EXPENSE_BUCKETS = [
  { key: "recurring_monthly_vendor", label: "Monthly recurring vendor cost" },
  { key: "recurring_yearly_amortised_vendor", label: "Yearly recurring vendor cost (÷12)" },
  { key: "domain_amortised_vendor", label: "Domain vendor cost (÷12)" },
];

// Categories for the per-bill breakdown (same 4 buckets as income, but
// listed bill-by-bill). Order matches the income section above.
const BILL_CATEGORIES = [
  { key: "recurring_monthly", label: "Monthly recurring" },
  { key: "recurring_yearly", label: "Yearly recurring" },
  { key: "domain", label: "Domains" },
  { key: "data_migration", label: "Data migration" },
];

// One colour per income bucket, shared by the pie slices and their
// legends so a slice always means the same thing in both currencies.
const BUCKET_COLORS = ["#134f8b", "#2f8fd4", "#5ec4b6", "#f0a63c"];

// Sortable columns for the per-currency "Revenue by bill" tables.
const REVENUE_BILL_COLUMNS = [
  { key: "created_at", label: "Date", type: "date" },
  { key: "customer_email", label: "Customer", type: "string" },
  { key: "workspace_name", label: "Workspace", type: "string" },
  { key: "domain", label: "Domain", type: "string" },
  { key: "plan_name", label: "Plan", type: "string" },
  { key: "quantity", label: "Qty", type: "number", numeric: true },
  {
    key: "marketplace_full_cycle",
    label: "Mkt rate",
    type: "number",
    numeric: true,
  },
  {
    key: "vendor_full_cycle",
    label: "Vendor cost",
    type: "number",
    numeric: true,
  },
  {
    key: "vendor_cost_updated_at",
    label: "Rate confirmed",
    type: "date",
  },
  { key: "amount", label: "Amount", type: "number", numeric: true },
];

// Open a subscription in the All Workspaces view, pinned + highlighted.
// Same deep-link the Overdue table and the ref-lookup search use.
function openSubscriptionTab(workspaceId, subscriptionId) {
  if (!workspaceId) return;
  const href = `/dashboard/myapps?workspaceId=${workspaceId}${
    subscriptionId ? `&highlightSub=${subscriptionId}` : ""
  }`;
  window.open(href, "_blank", "noopener,noreferrer");
}

function fmtConfirmedDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// Margin reconciliation — every subscription that contributed to the
// selected period, worst margin first.
const MARGIN_BY_SUB_COLUMNS = [
  { key: "workspace_name", label: "Workspace / Plan", type: "string" },
  { key: "bill_count", label: "Bills", type: "number", numeric: true },
  { key: "income", label: "Income", type: "number", numeric: true },
  { key: "vendor_cost", label: "Vendor cost", type: "number", numeric: true },
  { key: "margin", label: "Margin", type: "number", numeric: true },
  { key: "margin_pct", label: "%", type: "number", numeric: true },
];

/**
 * Per-subscription margin for the SELECTED period.
 *
 * This is the table that answers "the month says −63k but only two
 * subs are flagged as bleeding — where is the rest coming from?".
 *
 * The bleeding-subs panel below is a different scan with a different
 * window and its own filters, so it was never going to add up to the
 * month card. These rows are built from the exact bills that produced
 * the card, so Σ(margin) here IS the card's margin. Every rupee of the
 * gap is a named row.
 *
 * Three tags explain the rows the bleeding panel cannot show:
 *   carried    nothing raised this period — the tail of an older
 *              yearly bill still spreading over its 12 months.
 *   cancelled  the sub is gone, but its bills keep amortising.
 *   unpriced   no vendor rate anywhere, so it books at 100% margin.
 *              A blank, not a profit.
 */
function MarginBySubTable({ rows, cur }) {
  const { sortKey, sortDir, sortedRows, handleSort } = useSortableRows(
    rows,
    MARGIN_BY_SUB_COLUMNS,
    "margin",
    "asc",
  );
  return (
    <div className={styles.reconScroll}>
      <table className={styles.reconTable}>
      <thead>
        <SortableHeaderRow
          columns={MARGIN_BY_SUB_COLUMNS}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
        />
      </thead>
      <tbody>
        {sortedRows.map((r) => (
          <tr
            key={r.subscription_id}
            className={styles.reconRow}
            style={{ cursor: r.workspace_id ? "pointer" : "default" }}
            onClick={() =>
              openSubscriptionTab(r.workspace_id, r.subscription_id)
            }
            title={
              r.workspace_id
                ? "Open this subscription in All Workspaces"
                : undefined
            }
          >
            <td className={styles.reconCellMain}>
              <div className={styles.reconWs}>
                {r.workspace_name}
                {r.is_cancelled ? (
                  <span
                    className={styles.reconTag}
                    title="Cancelled today, but its bills are still amortising into this period. The bleeding-subs panel hides these on purpose — there is no lever left to pull — so they only show up here."
                  >
                    cancelled
                  </span>
                ) : null}
                {r.carried_in ? (
                  <span
                    className={styles.reconTag}
                    title="Nothing was raised in this period. This is the tail of an earlier bill still spreading over its cycle — which is why a 180-day scan cannot see it."
                  >
                    carried
                  </span>
                ) : null}
                {!r.vendor_priced ? (
                  <span
                    className={styles.reconTagWarn}
                    title="No vendor rate on this subscription and no snapshot on its bills, so it books at 100% margin. That is missing data, not profit — the real margin is lower by an unknown amount."
                  >
                    unpriced
                  </span>
                ) : null}
              </div>
              <div className={styles.reconPlan}>
                {r.plan_name}
                {r.interval
                  ? ` · ${r.n_interval || 1} ${r.interval}`
                  : ""}
                {r.quantity ? ` · qty ${r.quantity}` : ""}
              </div>
              {r.customer_email ? (
                <div className={styles.reconEmail}>{r.customer_email}</div>
              ) : null}
            </td>
            <td className={styles.reconCellNum}>{r.bill_count}</td>
            <td className={styles.reconCellNum}>
              {fmtBillAmount(r.income, cur)}
            </td>
            <td className={styles.reconCellNum}>
              {r.vendor_priced ? (
                fmtBillAmount(r.vendor_cost, cur)
              ) : (
                <span className={styles.reconMuted}>—</span>
              )}
            </td>
            <td
              className={styles.reconCellNum}
              style={{
                color: r.margin < 0 ? "#c0392b" : "#1b7a4b",
                fontWeight: 700,
              }}
            >
              {fmtBillAmount(r.margin, cur)}
            </td>
            <td className={styles.reconCellNum}>
              {r.margin_pct == null ? "—" : `${r.margin_pct}%`}
            </td>
          </tr>
        ))}
      </tbody>
      </table>
    </div>
  );
}

// Sortable columns for the negative-margin tables. Default sort is
// margin ascending — most negative (worst bleed) first.
const NEGATIVE_MARGIN_COLUMNS = [
  { key: "workspace_name", label: "Workspace / Plan", type: "string" },
  { key: "bills_count", label: "Bills", type: "number", numeric: true },
  { key: "revenue", label: "Revenue", type: "number", numeric: true },
  { key: "vendor_cost_total", label: "Vendor", type: "number", numeric: true },
  { key: "overpay_pct", label: "Over list", type: "number", numeric: true },
  { key: "margin", label: "Margin", type: "number", numeric: true },
];

/**
 * One currency's negative-margin sub list, with sortable headers.
 * Own sort state per currency so the INR and USD cards stay
 * independent.
 */
function NegativeMarginTable({ rows, cur }) {
  const { sortKey, sortDir, sortedRows, handleSort } = useSortableRows(
    rows,
    NEGATIVE_MARGIN_COLUMNS,
    "margin",
    "asc", // most negative first — the worst bleed leads
  );

  const metaLine = {
    display: "block",
    color: "#8696aa",
    fontSize: 11.5,
    marginTop: 2,
    wordBreak: "break-word",
  };

  return (
    <div className={styles.negTableScroll}>
      <table className={styles.negTable}>
        <thead>
          <SortableHeaderRow
            columns={NEGATIVE_MARGIN_COLUMNS}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
          />
        </thead>
        <tbody>
          {sortedRows.map((r) => {
            const cleanEmail = (r.owner_email || "")
              .replace(/^recurring/i, "")
              .trim();
            return (
              <tr key={r.subscription_id}>
                <td className={styles.negWho}>
                  <strong>
                    {r.workspace_name}
                    {r.is_cancelled ? (
                      <span
                        className={styles.reconTag}
                        style={{ marginLeft: 6 }}
                        title="Already cancelled — no lever left to pull, but its bills keep amortising into the monthly expense line for the rest of the cycle."
                      >
                        cancelled
                      </span>
                    ) : null}
                  </strong>
                  <span style={metaLine}>
                    {r.marketplace_name || "—"}
                    {r.interval
                      ? ` · ${r.n_interval || 1} ${r.interval}`
                      : ""}
                    {r.type ? ` · ${r.type}` : ""}
                  </span>
                  {r.domain ? (
                    <span
                      style={{
                        display: "inline-block",
                        marginTop: 4,
                        padding: "2px 8px",
                        background: "#e8f1fb",
                        color: "#134f8b",
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.2,
                      }}
                    >
                      🌐 {r.domain}
                    </span>
                  ) : null}
                  {cleanEmail ? <span style={metaLine}>{cleanEmail}</span> : null}
                  {r.quantity ? (
                    <span style={metaLine}>qty {r.quantity}</span>
                  ) : null}
                </td>
                <td className={styles.negNumeric}>{r.bills_count}</td>
                <td className={styles.negNumeric}>{fmt(r.revenue, cur)}</td>
                <td className={styles.negNumeric}>
                  {fmt(r.vendor_cost_total, cur)}
                </td>
                <td
                  className={`${styles.negNumeric} ${styles.bad}`}
                  title={
                    r.marketplace_per_cycle
                      ? `We pay ${fmt(r.vendor_cost_per_cycle, cur)} per cycle and sell it for ${fmt(r.marketplace_per_cycle, cur)}. Fix the vendor rate or the sell price — no billing period can make this one profitable.`
                      : undefined
                  }
                >
                  {r.overpay_pct != null ? `+${r.overpay_pct}%` : "—"}
                </td>
                <td className={`${styles.negNumeric} ${styles.bad}`}>
                  {fmt(r.margin, cur)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One currency's bill list inside a category, with sortable headers.
 * Extracted so each table owns its own sort state — sorting the INR
 * side must not reorder the USD side sitting next to it.
 */
function RevenueBillsTable({ rows, cur, fullWidth = false }) {
  const { sortKey, sortDir, sortedRows, handleSort } = useSortableRows(
    rows,
    REVENUE_BILL_COLUMNS,
    "created_at",
    "desc", // newest bill first, matching the previous implicit order
  );
  const subtotal = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div
      className={styles.billsTableScroll}
      // Inside the expanded modal the table gets the whole viewport, so
      // drop the 50%-column height cap that keeps the inline card short.
      style={fullWidth ? { maxHeight: "none" } : undefined}
    >
      <table className={styles.billsTable}>
        <thead>
          <SortableHeaderRow
            columns={REVENUE_BILL_COLUMNS}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
          />
        </thead>
        <tbody>
          {sortedRows.map((r) => (
            <tr
              key={r.bill_id}
              onClick={() =>
                openSubscriptionTab(r.workspace_id, r.subscription_id)
              }
              onKeyDown={(e) => {
                if (r.workspace_id && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  openSubscriptionTab(r.workspace_id, r.subscription_id);
                }
              }}
              tabIndex={r.workspace_id ? 0 : -1}
              title={
                r.workspace_id
                  ? "Open this subscription in a new tab"
                  : "No workspace to open — sub could not be resolved"
              }
              style={{ cursor: r.workspace_id ? "pointer" : "default" }}
            >
              <td>{fmtBillDate(r.created_at)}</td>
              <td className={styles.billsEmail}>
                {r.customer_email || (
                  <span className={styles.billsMuted}>—</span>
                )}
                {r.source === "payment" ? (
                  <span
                    className={`${styles.billsTag} ${styles.billsTagPayment}`}
                  >
                    payment
                  </span>
                ) : null}
                {r.sub_missing ? (
                  <span
                    className={`${styles.billsTag} ${styles.billsTagMissing}`}
                  >
                    missing sub
                  </span>
                ) : null}
              </td>
              <td>
                {r.workspace_name || (
                  <span className={styles.billsMuted}>—</span>
                )}
              </td>
              <td>
                {r.domain || <span className={styles.billsMuted}>—</span>}
              </td>
              <td>
                {r.plan_name || <span className={styles.billsMuted}>—</span>}
              </td>
              <td className={styles.billsNumeric}>{r.quantity ?? 1}</td>
              {/* Marketplace list value for one full cycle — what the
                  plan is priced at before any discount. */}
              <td className={styles.billsNumeric}>
                {r.marketplace_full_cycle != null ? (
                  <>
                    {fmtBillAmount(r.marketplace_full_cycle, cur)}
                    {/* A KNOWN discount — read off the Stripe invoice by
                        the backfill script. Red, because the vendor still
                        charges full and the gap is real margin loss. */}
                    {r.discount_amount ? (
                      <span
                        style={{
                          display: "block",
                          fontSize: 11,
                          color: "#c0392b",
                          fontWeight: 600,
                          marginTop: 2,
                        }}
                        title={`Stripe recorded a ${fmtBillAmount(
                          r.discount_amount,
                          cur,
                        )} discount on this invoice. The vendor still charges the full rate, so this comes out of margin.`}
                      >
                        −{fmtBillAmount(r.discount_amount, cur)} discount
                      </span>
                    ) : r.partial_cycle_pct ? (
                      /* Under list value, but we do NOT know why. A
                         mid-cycle "Add" covers only the remaining period,
                         which is normal and not a discount. Stated
                         neutrally in grey — the backfill script resolves
                         it properly from the Stripe line periods. */
                      <span
                        style={{
                          display: "block",
                          fontSize: 11,
                          color: "#8696aa",
                          fontWeight: 400,
                          marginTop: 2,
                        }}
                        title={`Billed ${fmtBillAmount(
                          r.full_amount,
                          cur,
                        )} against a full-cycle list value of ${fmtBillAmount(
                          r.marketplace_full_cycle,
                          cur,
                        )} — ${r.partial_cycle_pct}% of a cycle. Usually a mid-cycle seat addition covering only the remaining period. Run the vendor-cost backfill to resolve proration vs discount from the Stripe invoice.`}
                      >
                        {r.partial_cycle_pct}% of a full cycle
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span className={styles.billsMuted}>—</span>
                )}
              </td>
              {/* What the vendor charges us for one full cycle, and what
                  the expense line is actually counting for this row. */}
              <td className={styles.billsNumeric}>
                {r.vendor_full_cycle != null ? (
                  <>
                    {fmtBillAmount(r.vendor_full_cycle, cur)}
                    {r.vendor_cost_applied != null &&
                    Math.abs(r.vendor_cost_applied - r.vendor_full_cycle) >
                      0.5 ? (
                      <span
                        style={{
                          display: "block",
                          fontSize: 11,
                          color: "#8696aa",
                          fontWeight: 400,
                          marginTop: 2,
                        }}
                        title="What the expense line currently charges for this row. Differs from the full-cycle rate when the bill is prorated or discounted — the raise-time snapshot will remove this gap."
                      >
                        counted: {fmtBillAmount(r.vendor_cost_applied, cur)}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span className={styles.billsMuted}>—</span>
                )}
              </td>
              {/* When someone last confirmed this sub's vendor rate. */}
              <td>
                {fmtConfirmedDate(r.vendor_cost_updated_at) || (
                  <span
                    style={{
                      fontSize: 11,
                      color: "#c0392b",
                      fontWeight: 600,
                    }}
                    title="Nobody has confirmed this subscription's vendor rate since it was created. The expense figure for it is unverified."
                  >
                    never
                  </span>
                )}
              </td>
              <td className={styles.billsNumeric}>
                {fmtBillAmount(r.amount, cur)}
                {/* Amortised bill — show where the slice came from so a
                    ₹1,656 row against a ₹19,881 bill isn't a mystery. */}
                {r.cycle_months > 1 ? (
                  <span
                    style={{
                      display: "block",
                      fontSize: 11,
                      color: "#8696aa",
                      fontWeight: 400,
                      marginTop: 2,
                    }}
                    title={`Bill total ${fmtBillAmount(
                      r.full_amount,
                      cur,
                    )} spread over ${r.cycle_months} months${
                      r.months_in_window > 1
                        ? `; ${r.months_in_window} of them fall in this period`
                        : ""
                    }`}
                  >
                    {fmtBillAmount(r.full_amount, cur)} ÷ {r.cycle_months}
                    {r.months_in_window > 1 ? ` × ${r.months_in_window}` : ""}
                  </span>
                ) : null}
                {r.raised_in_period === false ? (
                  <span
                    style={{
                      display: "block",
                      fontSize: 10.5,
                      color: "#a0aec0",
                      fontWeight: 400,
                    }}
                  >
                    carried in
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className={styles.billsSubtotalRow}>
            <td colSpan={REVENUE_BILL_COLUMNS.length - 2}>
              Subtotal · {rows.length} bill{rows.length === 1 ? "" : "s"}
            </td>
            <td></td>
            <td className={styles.billsNumeric}>
              {fmtBillAmount(subtotal, cur)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// --- Period presets -------------------------------------------------
// Every range is MONTH-GRANULAR. A yearly bill is recognised 1/12 per
// whole month, so a part-month has no defined answer for the amortised
// buckets — and every preset below is month-aligned anyway.
//
// Financial year is the Indian one: 1 Apr → 31 Mar. FY 2026-27 means
// Apr 2026 through Mar 2027.

const mk = (y, m) => `${y}-${String(m).padStart(2, "0")}`;

/** Shift a {y,m} pair by n months. */
function shiftMonth(y, m, n) {
  const total = y * 12 + (m - 1) + n;
  return { y: Math.floor(total / 12), m: (total % 12) + 1 };
}

/** The FY a given month belongs to. Returns the starting calendar year. */
function fyStartYear(y, m) {
  return m >= 4 ? y : y - 1;
}

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "Apr 2026 – Mar 2027" from two YYYY-MM keys. */
function describeRange(from, to) {
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  const a = `${MONTH_ABBR[fm - 1]} ${fy}`;
  const b = `${MONTH_ABBR[tm - 1]} ${ty}`;
  return a === b ? a : `${a} – ${b}`;
}

/**
 * Build every preset relative to today. Returns descriptors the picker
 * renders directly, each carrying the exact months it resolves to so
 * the label can spell them out — no guessing which quarter convention
 * we used.
 */
function buildPeriodPresets(today) {
  const y = today.getFullYear();
  const m = today.getMonth() + 1;

  // Quarters follow the Indian FY: Q1 Apr-Jun, Q2 Jul-Sep, Q3 Oct-Dec,
  // Q4 Jan-Mar. Derive the current quarter's first month from that.
  const fyStart = fyStartYear(y, m);
  const monthsIntoFy = (y * 12 + m) - (fyStart * 12 + 4);
  const qIndex = Math.floor(monthsIntoFy / 3); // 0..3
  const qFirst = shiftMonth(fyStart, 4, qIndex * 3);
  const qLast = shiftMonth(qFirst.y, qFirst.m, 2);
  const pqFirst = shiftMonth(qFirst.y, qFirst.m, -3);
  const pqLast = shiftMonth(pqFirst.y, pqFirst.m, 2);

  const presets = [
    { key: "this_month", label: "This month", period: "month", year: y, month: m },
    {
      key: "last_month",
      label: "Last month",
      period: "month",
      ...(() => {
        const p = shiftMonth(y, m, -1);
        return { year: p.y, month: p.m };
      })(),
    },
    {
      key: "this_quarter",
      label: "This quarter",
      period: "range",
      from: mk(qFirst.y, qFirst.m),
      to: mk(qLast.y, qLast.m),
    },
    {
      key: "last_quarter",
      label: "Last quarter",
      period: "range",
      from: mk(pqFirst.y, pqFirst.m),
      to: mk(pqLast.y, pqLast.m),
    },
    {
      key: "this_fy",
      label: `This financial year (FY ${String(fyStart).slice(2)}-${String(fyStart + 1).slice(2)})`,
      period: "range",
      from: mk(fyStart, 4),
      to: mk(fyStart + 1, 3),
    },
    {
      key: "last_fy",
      label: `Previous financial year (FY ${String(fyStart - 1).slice(2)}-${String(fyStart).slice(2)})`,
      period: "range",
      from: mk(fyStart - 1, 4),
      to: mk(fyStart, 3),
    },
    { key: "calendar_year", label: "Calendar year", period: "year", year: y },
    { key: "custom", label: "Custom range…", period: "range", custom: true },
  ];

  // Spell out the months each range covers so nobody has to guess.
  return presets.map((p) =>
    p.period === "range" && p.from && p.to
      ? { ...p, label: `${p.label} · ${describeRange(p.from, p.to)}` }
      : p,
  );
}

const RevenueView = () => {
  const now = new Date();
  const [period, setPeriod] = useState("month");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  // Active preset + the resolved month range when period === "range".
  const [presetKey, setPresetKey] = useState("this_month");
  const [rangeFrom, setRangeFrom] = useState(mk(now.getFullYear(), 1));
  const [rangeTo, setRangeTo] = useState(
    mk(now.getFullYear(), now.getMonth() + 1),
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [negData, setNegData] = useState(null);
  const [negLoading, setNegLoading] = useState(false);
  // Cancelled subs are held out of the priced-below-cost list by
  // default — nothing can be fixed on a sub that will never bill
  // again. Flipping this asks the API for them anyway.
  const [showCancelledBelowCost, setShowCancelledBelowCost] = useState(false);
  const [billsData, setBillsData] = useState(null);
  const [billsLoading, setBillsLoading] = useState(false);
  // Which bill table (if any) is blown up to full width. Holds the rows
  // + currency + category label so the modal can render without
  // re-deriving them. null = closed.
  const [expandedTable, setExpandedTable] = useState(null);
  // Bumped to force the bills drill-down to refetch alongside the
  // headline numbers (manual Refresh, and tab-focus refetch).
  const [billsRefreshToken, setBillsRefreshToken] = useState(0);

  // One place that turns the current selection into query params, so
  // the revenue call and the bills call can never disagree about which
  // period they are showing.
  const periodQuery = useMemo(() => {
    if (period === "range") {
      return `period=range&from=${encodeURIComponent(rangeFrom)}&to=${encodeURIComponent(rangeTo)}`;
    }
    if (period === "year") return `period=year&year=${year}`;
    return `period=month&year=${year}&month=${month}`;
  }, [period, year, month, rangeFrom, rangeTo]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_SERVER_URL}/admin/revenue?${periodQuery}`;
      const res = await fetch(url, { credentials: "include" });
      const json = await res.json();
      if (json?.status === 200) setData(json.data);
      else toast.error(json?.msg || "Failed to load revenue");
    } catch (e) {
      console.error("[revenue]", e);
      toast.error("Failed to load revenue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodQuery]);

  // Refetch when this tab regains focus.
  //
  // The common workflow is: edit a subscription's vendor cost in another
  // tab, switch back here, expect the numbers to have moved. Without
  // this the tracker sits on whatever it loaded minutes ago and you have
  // to hit Refresh or reload to see your own edit.
  //
  // Throttled to once per 15s so flicking between tabs doesn't hammer
  // the endpoint — buildAttributionIndex walks every bill, it is not a
  // cheap call.
  const lastFetchedAtRef = React.useRef(Date.now());
  useEffect(() => {
    const REFETCH_AFTER_MS = 15000;
    const onFocus = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - lastFetchedAtRef.current < REFETCH_AFTER_MS) return;
      lastFetchedAtRef.current = Date.now();
      fetchData();
      setBillsRefreshToken((t) => t + 1);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodQuery]);

  // Negative-margin scan — independent of the period picker (these are
  // structural losses, not bound to a calendar month). Refreshed
  // alongside the main data so a manual Refresh updates both.
  useEffect(() => {
    const fetchNeg = async () => {
      setNegLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/admin/revenue/negative_margins?lookback=365&include_cancelled=${
            showCancelledBelowCost ? 1 : 0
          }`,
          { credentials: "include" },
        );
        const json = await res.json();
        if (json?.status === 200) setNegData(json.data);
      } catch (e) {
        console.error("[revenue:neg]", e);
      } finally {
        setNegLoading(false);
      }
    };
    fetchNeg();
  }, [showCancelledBelowCost]);

  // Per-bill breakdown — bound to the SAME period selector as the main
  // revenue view, so it re-fetches whenever period/year/month changes.
  useEffect(() => {
    const fetchBills = async () => {
      setBillsLoading(true);
      try {
        const url = `${process.env.REACT_APP_SERVER_URL}/admin/revenue/bills?${periodQuery}`;
        const res = await fetch(url, { credentials: "include" });
        const json = await res.json();
        if (json?.data) setBillsData(json.data);
        else setBillsData(null);
      } catch (e) {
        console.error("[revenue:bills]", e);
        setBillsData(null);
      } finally {
        setBillsLoading(false);
      }
    };
    fetchBills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodQuery, billsRefreshToken]);

  // Resolved once per mount — presets are relative to today, and today
  // does not change mid-session in any way that matters here.
  const periodPresets = useMemo(() => buildPeriodPresets(new Date()), []);

  // Human label for the active period, reused in the section headings.
  const periodLabel = useMemo(() => {
    if (period === "range") return describeRange(rangeFrom, rangeTo);
    if (period === "year") return String(year);
    return `${MONTHS[month - 1]} ${year}`;
  }, [period, year, month, rangeFrom, rangeTo]);

  const yearOptions = useMemo(() => {
    const list = [];
    for (let y = now.getFullYear(); y >= 2023; y--) list.push(y);
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byCurrency =
    period === "month"
      ? data?.by_currency || {}
      : data?.yearly_by_currency || {};
  const currencies = Object.keys(byCurrency).sort();

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>Revenue Tracker</h1>
          <div className={styles.subtitle}>
            Income · vendor expense · margin. Bills split into 4 buckets so
            yearly amortisation and one-shot data-migration revenue stay
            visible separately. INR + USD shown side-by-side, never mixed.
          </div>
        </div>
        <div className={styles.controls}>
          <ToggleButtonGroup
            value={period}
            exclusive
            size="small"
            onChange={(_e, v) => v && setPeriod(v)}
            sx={{
              background: "#fff",
              borderRadius: "999px",
              padding: "3px",
              border: "1px solid #d8e2ec",
              boxShadow: "0 1px 3px rgba(15,27,44,0.06)",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "999px !important",
                px: 2.2,
                py: 0.6,
                fontWeight: 700,
                fontSize: "12.5px",
                color: "#607084",
                textTransform: "none",
                letterSpacing: "0.02em",
                "&:hover": { background: "#f1f6fb" },
                "&.Mui-selected": {
                  background: "#134f8b",
                  color: "#fff",
                  "&:hover": { background: "#0f3f6e" },
                },
              },
            }}
          >
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="year">Year</ToggleButton>
          </ToggleButtonGroup>

          {/* Presets — quarter / financial year / custom range. Picking
              one drives period + the resolved months; the Month/Year
              toggle above still works for ad-hoc single periods. */}
          <FormControl
            size="small"
            sx={{ minWidth: 250, background: "#fff", borderRadius: "999px" }}
          >
            <InputLabel id="preset-label">Quick range</InputLabel>
            <Select
              labelId="preset-label"
              label="Quick range"
              value={presetKey}
              onChange={(e) => {
                const key = e.target.value;
                setPresetKey(key);
                const p = periodPresets.find((x) => x.key === key);
                if (!p) return;
                if (p.custom) {
                  setPeriod("range");
                  return; // keep whatever from/to the pickers hold
                }
                setPeriod(p.period);
                if (p.period === "range") {
                  setRangeFrom(p.from);
                  setRangeTo(p.to);
                } else {
                  if (p.year != null) setYear(p.year);
                  if (p.month != null) setMonth(p.month);
                }
              }}
            >
              {periodPresets.map((p) => (
                <MenuItem key={p.key} value={p.key}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Custom range — month granular, see the note on
              buildPeriodPresets for why. */}
          {period === "range" && presetKey === "custom" && (
            <>
              <TextField
                size="small"
                type="month"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={rangeFrom}
                onChange={(e) => setRangeFrom(e.target.value)}
                sx={{ background: "#fff", borderRadius: "999px", minWidth: 150 }}
              />
              <TextField
                size="small"
                type="month"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={rangeTo}
                onChange={(e) => setRangeTo(e.target.value)}
                sx={{ background: "#fff", borderRadius: "999px", minWidth: 150 }}
              />
            </>
          )}

          {period === "month" && (
            <FormControl size="small" sx={{ minWidth: 110, background: "#fff", borderRadius: "999px" }}>
              <InputLabel id="month-label">Month</InputLabel>
              <Select
                labelId="month-label"
                label="Month"
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                sx={{ borderRadius: "999px" }}
              >
                {MONTHS.map((m, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 110, background: "#fff", borderRadius: "999px" }}>
            <InputLabel id="year-label">Year</InputLabel>
            <Select
              labelId="year-label"
              label="Year"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              sx={{ borderRadius: "999px" }}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            className={styles.refreshBtn}
            startIcon={<RefreshOutlinedIcon />}
            onClick={fetchData}
          >
            Refresh
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className={styles.loaderWrap}>
          <CircularProgress />
        </div>
      ) : currencies.length === 0 ? (
        <div className={styles.emptyCurrencyState}>
          No bills found for this period.
        </div>
      ) : (
        <>
          <div className={styles.currencyRow}>
            {currencies.map((code) => {
              const block = byCurrency[code];
              const margin = Number(block.margin || 0);
              const isNeg = margin < 0;
              return (
                <div key={code} className={styles.currencyCard}>
                  <div className={styles.currencyHeader}>
                    <div className={styles.currencyLabel}>
                      {code} ·{" "}
                      {periodLabel}
                    </div>
                    <div className={styles.currencyMargin}>
                      {fmt(block.total_income, code)}
                    </div>
                    <div className={styles.currencyTopline}>
                      <span>
                        Expense: <strong>{fmt(block.total_expense, code)}</strong>
                      </span>
                      <span>
                        Margin: <strong>{fmt(margin, code)}</strong>
                      </span>
                    </div>
                  </div>
                  <div className={styles.bucketSection}>
                    <div className={styles.bucketSectionTitle}>Income</div>
                    {INCOME_BUCKETS.map((b) => (
                      <div key={b.key} className={styles.bucketRow}>
                        <div>
                          <div className={styles.bucketLabel}>{b.label}</div>
                          <span className={styles.bucketHint}>{b.hint}</span>
                        </div>
                        <div className={styles.bucketCount}></div>
                        <div className={styles.bucketValue}>
                          {fmt(block.income?.[b.key] || 0, code)}
                        </div>
                      </div>
                    ))}
                    <div className={styles.totalRow}>
                      <div className={styles.totalLabel}>Total income</div>
                      <div></div>
                      <div className={styles.totalValue}>
                        {fmt(block.total_income, code)}
                      </div>
                    </div>

                    <div className={styles.bucketSectionTitle} style={{ marginTop: 14 }}>
                      Vendor expense
                    </div>
                    {EXPENSE_BUCKETS.map((b) => (
                      <div key={b.key} className={styles.bucketRow}>
                        <div className={styles.bucketLabel}>{b.label}</div>
                        <div className={styles.bucketCount}></div>
                        <div className={`${styles.bucketValue} ${styles.expenseValue}`}>
                          –{fmt(block.expense?.[b.key] || 0, code)}
                        </div>
                      </div>
                    ))}
                    <div className={styles.totalRow}>
                      <div className={styles.totalLabel}>Total expense</div>
                      <div></div>
                      <div className={styles.totalValue}>
                        –{fmt(block.total_expense, code)}
                      </div>
                    </div>

                    <div
                      className={`${styles.marginRow} ${isNeg ? styles.marginRowNeg : ""}`}
                    >
                      <span className={styles.marginLabel}>Margin</span>
                      <span className={styles.marginAmount}>{fmt(margin, code)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {data?.debug ? (
            <div className={styles.debugNote}>
              Pulled {data.debug.bills_total} bill{data.debug.bills_total === 1 ? "" : "s"} from DB
              {data.debug.skipped_no_sub
                ? ` · skipped ${data.debug.skipped_no_sub} (no matching subscription)`
                : ""}
              {" · a bill counts in the month it was raised, regardless of the subscription's status today"}
            </div>
          ) : null}

          {/* Margin reconciliation. Built from the SAME bills as the
              cards above, so Σ(margin) here equals the card's margin.
              This is what the bleeding-subs panel below cannot tell
              you: that panel is a separate 365-day scan of actionable
              subs, this one accounts for the selected period exactly. */}
          <div className={styles.reconSection}>
            <h2 className={styles.reconTitle}>
              Where the margin comes from · {periodLabel}
            </h2>
            <p className={styles.reconSub}>
              Every subscription that contributed to this period, worst
              margin first. These rows are built from the same bills as
              the cards above, so they add up to the margin shown there
              — unlike the bleeding-subs scan below, which uses its own
              window. Click a row to open the subscription.
            </p>
            {billsLoading && !billsData ? (
              <div className={styles.loaderWrap}>
                <CircularProgress size={20} />
              </div>
            ) : (
              <div className={styles.reconTablesRow}>
                {["INR", "USD"].map((cur) => {
                  const block = billsData?.by_currency?.[cur];
                  const rows = block?.margin_by_sub || [];
                  const sum = block?.margin_summary;
                  if (!rows.length) return null;
                  return (
                    <div key={cur} className={styles.reconCard}>
                      <div className={styles.reconCardHeader}>
                        <div className={styles.reconCardLabel}>
                          {cur} · {rows.length} sub
                          {rows.length === 1 ? "" : "s"}
                        </div>
                        <div
                          className={`${styles.reconCardTotal} ${
                            (sum?.total_margin || 0) < 0 ? styles.bad : ""
                          }`}
                        >
                          Margin: {fmt(sum?.total_margin || 0, cur)}
                        </div>
                      </div>
                      {sum ? (
                        <div
                          className={styles.reconStrip}
                          style={{ padding: "12px 16px 0", margin: 0 }}
                        >
                          <div className={styles.reconChip}>
                            <span
                              className={styles.reconChipLabel}
                              title="Subscriptions whose bills lost money INSIDE this period. Different from the priced-below-cost list further down, which ignores periods entirely."
                            >
                              Lost money this period
                            </span>
                            <span
                              className={`${styles.reconChipValue} ${styles.bad}`}
                            >
                              {sum.bleeding_subs}
                            </span>
                            <span className={styles.reconChipNote}>
                              {fmt(sum.bleeding_total, cur)} total
                            </span>
                          </div>
                          <div className={styles.reconChip}>
                            <span className={styles.reconChipLabel}>
                              …of which cancelled
                            </span>
                            <span className={styles.reconChipValue}>
                              {sum.bleeding_cancelled}
                            </span>
                            <span className={styles.reconChipNote}>
                              {fmt(sum.bleeding_cancelled_total, cur)} · hidden
                              from the panel below
                            </span>
                          </div>
                          <div className={styles.reconChip}>
                            <span className={styles.reconChipLabel}>
                              …of which carried in
                            </span>
                            <span className={styles.reconChipValue}>
                              {sum.bleeding_carried_in}
                            </span>
                            <span className={styles.reconChipNote}>
                              {fmt(sum.bleeding_carried_in_total, cur)} · older
                              bills still amortising
                            </span>
                          </div>
                          <div className={styles.reconChip}>
                            <span className={styles.reconChipLabel}>
                              Unpriced revenue
                            </span>
                            <span className={styles.reconChipValue}>
                              {sum.unpriced_subs}
                            </span>
                            <span className={styles.reconChipNote}>
                              {fmt(sum.unpriced_income, cur)} booked at 100%
                              margin — no vendor rate
                            </span>
                          </div>
                        </div>
                      ) : null}
                      <MarginBySubTable rows={rows} cur={cur} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {Array.isArray(data?.debug?.classification) &&
          data.debug.classification.length ? (
            <details style={{ marginTop: 16, fontSize: 12.5 }}>
              <summary
                style={{
                  cursor: "pointer",
                  color: "#607084",
                  fontWeight: 600,
                  padding: "8px 0",
                }}
              >
                Show subscription classification ({data.debug.classification.length} distinct shapes)
              </summary>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: 8,
                  background: "#fff",
                  borderRadius: 8,
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(15,27,44,0.04)",
                }}
              >
                <thead style={{ background: "#f7fbff" }}>
                  <tr style={{ textAlign: "left" }}>
                    <th style={{ padding: 8, fontSize: 11, color: "#607084" }}>type</th>
                    <th style={{ padding: 8, fontSize: 11, color: "#607084" }}>interval</th>
                    <th style={{ padding: 8, fontSize: 11, color: "#607084" }}>marketplaceName</th>
                    <th style={{ padding: 8, fontSize: 11, color: "#607084" }}>→ bucket</th>
                    <th style={{ padding: 8, fontSize: 11, color: "#607084", textAlign: "right" }}>bills</th>
                    <th style={{ padding: 8, fontSize: 11, color: "#607084", textAlign: "right" }}>amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.debug.classification.map((c, i) => (
                    <tr key={i} style={{ borderTop: "1px solid #f0f5fa" }}>
                      <td style={{ padding: 8 }}>{c.sub_type || <em style={{ color: "#bbb" }}>null</em>}</td>
                      <td style={{ padding: 8 }}>{c.sub_interval || <em style={{ color: "#bbb" }}>null</em>}</td>
                      <td style={{ padding: 8 }}>{c.sub_marketplace_name || <em style={{ color: "#bbb" }}>null</em>}</td>
                      <td style={{ padding: 8, fontWeight: 700 }}>{c.category}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{c.bills}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{c.amount?.toLocaleString?.("en-IN") || c.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          ) : null}

          {/* Negative-margin tables — independent of the period
              picker. Subs where vendor cost outran revenue in the
              lookback window, split by currency.

              Window is 365 days, not 180: a yearly subscription bills
              ONCE a year, so half of them had no bill inside a
              six-month window and simply never appeared here — while
              their bills kept amortising into every month's expense
              line. That gap is what made the yearly bucket look
              unexplained. */}
          <div className={styles.negSection}>
            <h2 className={styles.negTitle}>Subscriptions priced below cost</h2>
            <p className={styles.negSub}>
              Subscriptions where the vendor rate is HIGHER than the
              price we sell at — Σ(qty × vendor_price) exceeds
              Σ(qty × marketplace_price) per cycle. Every bill one of
              these raises loses money, whatever period you look at, so
              this is a pricing error to correct rather than a month to
              explain. Scanned over the last 365 days because a yearly
              sub only bills once.
            </p>
            <p className={styles.negSub} style={{ marginTop: -6 }}>
              <strong>This count will not match the table above, and
              should not.</strong>{" "}
              That table asks “which subs lost money in{" "}
              {periodLabel}?” — a sub priced below cost only appears
              there if it actually billed into that period, so a yearly
              one often will not. This list asks “which subs are
              mispriced right now?”. Fix the rate here; read the month
              there.
            </p>
            {/* Cancelled subs are excluded by default: there is no rate
                left to renegotiate on one, so listing it turns a work
                queue into a history lesson. Counted, never hidden. */}
            {negData?.cancelled_excluded > 0 || showCancelledBelowCost ? (
              <p
                className={styles.negSub}
                style={{ marginTop: -6, fontSize: 12.5 }}
              >
                {showCancelledBelowCost ? (
                  <>
                    Showing cancelled subscriptions too. They will never
                    bill again, so nothing here can be fixed — their
                    effect on a given month is in the table above,
                    tagged <em>cancelled</em>.{" "}
                  </>
                ) : (
                  <>
                    {negData.cancelled_excluded} cancelled subscription
                    {negData.cancelled_excluded === 1 ? " is" : "s are"}{" "}
                    also priced below cost and not listed — a cancelled
                    sub raises no further bills, so there is no rate to
                    fix.{" "}
                  </>
                )}
                <button
                  type="button"
                  onClick={() =>
                    setShowCancelledBelowCost((v) => !v)
                  }
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "#134f8b",
                    fontWeight: 700,
                    cursor: "pointer",
                    font: "inherit",
                    textDecoration: "underline",
                  }}
                >
                  {showCancelledBelowCost ? "Hide them" : "Show them anyway"}
                </button>
              </p>
            ) : null}
            {negLoading && !negData ? (
              <div className={styles.loaderWrap}>
                <CircularProgress size={20} />
              </div>
            ) : (
              <div className={styles.negTablesRow}>
                {["INR", "USD"].map((cur) => {
                  const rows = negData?.by_currency?.[cur] || [];
                  const totalLoss = rows.reduce((s, r) => s + Number(r.margin || 0), 0);
                  return (
                    <div key={cur} className={styles.negCard}>
                      <div className={styles.negCardHeader}>
                        <div className={styles.negCardLabel}>{cur} · priced below cost</div>
                        <div className={styles.negCardCount}>
                          {rows.length} sub{rows.length === 1 ? "" : "s"}
                        </div>
                        <div className={styles.negCardTotal}>
                          Total bleed: {fmt(totalLoss, cur)}
                        </div>
                      </div>
                      {rows.length === 0 ? (
                        <div className={styles.negEmpty}>
                          None — every {cur} sub sells at or above what
                          the vendor charges us.
                        </div>
                      ) : (
                        <NegativeMarginTable rows={rows} cur={cur} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Revenue we cannot cost at all. Not losses — blind
                spots. Every row here is currently booked at 100%
                margin purely because no vendor rate exists, so this
                doubles as the rate-cleanup work queue. */}
            {negData &&
            Object.values(negData.unpriced_by_currency || {}).some(
              (a) => a.length,
            ) ? (
              <details style={{ marginTop: 14, fontSize: 13 }}>
                <summary
                  style={{
                    cursor: "pointer",
                    color: "#b26a00",
                    fontWeight: 700,
                    padding: "8px 0",
                  }}
                >
                  {Object.values(negData.unpriced_by_currency).reduce(
                    (n, a) => n + a.length,
                    0,
                  )}{" "}
                  subscription(s) have revenue but no vendor rate — booked
                  at 100% margin
                </summary>
                <div className={styles.reconTablesRow}>
                  {Object.entries(negData.unpriced_by_currency).map(
                    ([cur, list]) =>
                      list.length ? (
                        <div key={cur} className={styles.reconCard}>
                          <div className={styles.reconCardHeader}>
                            <div className={styles.reconCardLabel}>
                              {cur} · unpriced
                            </div>
                            <div className={styles.reconCardTotal}>
                              {fmt(
                                list.reduce(
                                  (t, r) => t + Number(r.revenue || 0),
                                  0,
                                ),
                                cur,
                              )}{" "}
                              uncosted
                            </div>
                          </div>
                          <div className={styles.reconScroll}>
                            <table className={styles.reconTable}>
                              <thead>
                                <tr>
                                  <th>Workspace / Plan</th>
                                  <th>Why</th>
                                  <th style={{ textAlign: "right" }}>
                                    Revenue
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {list.map((r) => (
                                  <tr
                                    key={r.subscription_id}
                                    className={styles.reconRow}
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      openSubscriptionTab(
                                        r.workspace_id,
                                        r.subscription_id,
                                      )
                                    }
                                  >
                                    <td className={styles.reconCellMain}>
                                      <div className={styles.reconWs}>
                                        {r.workspace_name}
                                        {r.is_cancelled ? (
                                          <span className={styles.reconTag}>
                                            cancelled
                                          </span>
                                        ) : null}
                                      </div>
                                      <div className={styles.reconPlan}>
                                        {r.marketplace_name}
                                        {r.interval
                                          ? ` · ${r.n_interval || 1} ${r.interval}`
                                          : ""}
                                      </div>
                                    </td>
                                    <td>
                                      <span className={styles.reconTagWarn}>
                                        {r.reason}
                                      </span>
                                    </td>
                                    <td className={styles.reconCellNum}>
                                      {fmtBillAmount(r.revenue, cur)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : null,
                  )}
                </div>
              </details>
            ) : null}
            {negData && (
              <div className={styles.debugNote}>
                Scanned {negData.subs_scanned} sub{negData.subs_scanned === 1 ? "" : "s"} ·{" "}
                {negData.bills_scanned} bill{negData.bills_scanned === 1 ? "" : "s"} from last{" "}
                {negData.lookback_days} days
                {negData.cancelled_excluded
                  ? ` · ${negData.cancelled_excluded} cancelled sub${
                      negData.cancelled_excluded === 1 ? "" : "s"
                    } held back`
                  : ""}
              </div>
            )}
          </div>

          {/* Income mix — one pie per currency, over the SAME selected
              period. Slice colours are fixed per bucket so INR and USD
              read against each other at a glance. */}
          <div className={styles.pieSection}>
            <h2 className={styles.pieTitle}>Revenue mix</h2>
            <p className={styles.pieSub}>
              Share of income by bucket for{" "}
              {period === "month" ? `${MONTHS[month - 1]} ${year}` : `${year}`}
              . Amortised values — the same numbers as the cards above.
            </p>
            <div className={styles.pieRow}>
              {["INR", "USD"].map((code) => {
                const block = byCurrency[code];
                const series = INCOME_BUCKETS.map((b) =>
                  Number(block?.income?.[b.key] || 0),
                );
                const total = series.reduce((a, b) => a + b, 0);
                return (
                  <div key={code} className={styles.pieCard}>
                    <div className={styles.pieCardHeader}>
                      <span className={styles.pieCardLabel}>{code}</span>
                      <span className={styles.pieCardTotal}>
                        {fmt(total, code)}
                      </span>
                    </div>
                    {total <= 0 ? (
                      <div className={styles.pieEmpty}>
                        No {code} revenue in this period.
                      </div>
                    ) : (
                      <ReactApexChart
                        type="pie"
                        height={300}
                        series={series}
                        options={{
                          chart: { toolbar: { show: false } },
                          labels: INCOME_BUCKETS.map((b) => b.label),
                          colors: BUCKET_COLORS,
                          legend: {
                            position: "bottom",
                            fontSize: "12px",
                            fontFamily:
                              '"League Spartan", "Inter", system-ui, sans-serif',
                          },
                          dataLabels: {
                            formatter: (pct) => `${Number(pct).toFixed(1)}%`,
                          },
                          stroke: { width: 2, colors: ["#fff"] },
                          tooltip: {
                            y: { formatter: (v) => fmt(v, code) },
                          },
                          // Zero buckets would otherwise render as a
                          // 0% sliver plus a legend entry that means
                          // nothing.
                          noData: { text: "No revenue" },
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Revenue by bill — the SAME selected period as the revenue
              view above, broken down bill-by-bill within each of the 4
              categories, INR left / USD right (mirrors the split above). */}
          <div className={styles.billsSection}>
            <h2 className={styles.billsTitle}>Revenue by bill</h2>
            <p className={styles.billsSub}>
              Every bill contributing to{" "}
              {periodLabel}
              . INR on the left, USD on the right. Amount is what the bill
              contributes to this {period === "month" ? "month" : "period"} —
              for yearly and domain bills that is the ÷12 slice, so a bill
              raised months ago still appears here until its 12 months are
              up. Each subtotal matches the matching income line above.
            </p>
            {billsLoading && !billsData ? (
              <div className={styles.loaderWrap}>
                <CircularProgress size={20} />
              </div>
            ) : (
              BILL_CATEGORIES.map((cat) => (
                <div key={cat.key} className={styles.billsCategory}>
                  <div className={styles.billsCategoryHeader}>{cat.label}</div>
                  <div className={styles.billsTablesRow}>
                    {["INR", "USD"].map((cur) => {
                      const rows =
                        billsData?.by_currency?.[cur]?.[cat.key] || [];
                      return (
                        <div key={cur} className={styles.billsCard}>
                          <div className={styles.billsCardHeader}>
                            <span className={styles.billsCardLabel}>
                              {cur}
                            </span>
                            <span className={styles.billsCardCount}>
                              {rows.length} bill{rows.length === 1 ? "" : "s"}
                            </span>
                            {rows.length > 0 ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedTable({
                                    rows,
                                    cur,
                                    label: cat.label,
                                  })
                                }
                                title="Open this table full width"
                                style={{
                                  marginLeft: "auto",
                                  border: "1px solid #cfd8e5",
                                  background: "#f7fbff",
                                  color: "#134f8b",
                                  borderRadius: 6,
                                  padding: "2px 10px",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                ⤢ Expand
                              </button>
                            ) : null}
                          </div>
                          {rows.length === 0 ? (
                            <div className={styles.billsEmpty}>No bills</div>
                          ) : (
                            <RevenueBillsTable rows={rows} cur={cur} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Full-width view of a single bill table. The inline cards sit
              at 50% width so INR and USD can be compared side by side;
              this is the escape hatch when you need every column at once. */}
          <Dialog
            open={Boolean(expandedTable)}
            onClose={() => setExpandedTable(null)}
            maxWidth={false}
            fullWidth
            PaperProps={{
              sx: { width: "96vw", maxWidth: "96vw", m: 2 },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pr: 1,
                fontWeight: 700,
              }}
            >
              <span>
                {expandedTable?.label} · {expandedTable?.cur} ·{" "}
                {expandedTable?.rows?.length || 0} bill
                {expandedTable?.rows?.length === 1 ? "" : "s"}
                <span
                  style={{
                    fontWeight: 400,
                    fontSize: 13,
                    color: "#607084",
                    marginLeft: 10,
                  }}
                >
                  {periodLabel}
                  {" · click any row to open its subscription"}
                </span>
              </span>
              <IconButton onClick={() => setExpandedTable(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {expandedTable ? (
                <RevenueBillsTable
                  rows={expandedTable.rows}
                  cur={expandedTable.cur}
                  fullWidth
                />
              ) : null}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};


const TABS = [
  { key: "revenue", label: "Revenue tracker" },
  { key: "overdue", label: "Overdue amount" },
];

// Section shell. The month/year picker belongs to the revenue view only —
// overdue is a running balance, so it has no period of its own.
const RevenueTracker = () => {
  const [tab, setTab] = useState("revenue");

  return (
    <div className={styles.page}>
      <div className={styles.sectionTabs}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${styles.sectionTab} ${
              tab === t.key ? styles.sectionTabActive : ""
            }`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "revenue" ? <RevenueView /> : <OverdueAmount />}
    </div>
  );
};

export default RevenueTracker;
